import React from 'react';
import {
  InputProps,
  TokenProps,
  TypeaheadMenuProps,
  TypeaheadResult,
} from 'react-bootstrap-typeahead';
import { InputComponent } from '../inputTypes';
import {
  ClearFiltersButton,
  DisabledMenuItem,
  HiddenMenuDescription,
  Option as OptionType,
  StyledMenu,
  StyledMenuItem,
  StyledToken,
  StyledTypeahead,
} from './styles';
import { MultiTypeAheadTokenProps } from './types';

export type Option = OptionType;

export type MultiselectTypeaheadInputProps = {
  id: string;
  options: Option[];
  selectedOptions: Option[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<Option[]>>;
  minLenght?: number;
  maxResults?: number;
  noMatchesText?: string;
  hideOptionsByDefault?: boolean;
} & Omit<InputProps, 'value'>;

export const MultiselectTypeaheadInput: InputComponent<MultiselectTypeaheadInputProps> = ({
  id,
  options,
  selectedOptions,
  setSelectedOptions,
  minLength = 0,
  maxResults = 8,
  noMatchesText = 'No matches found',
  hideOptionsByDefault = false,
  ...rest
}) => {
  const menuDescriptionId = `${id}-typeahead-menu-description`;
  const [isInputEmpty, setIsInputEmpty] = React.useState(true);

  const handleOnInputChange: (input: string, e: Event) => void = (input) => {
    if (input.length === 0 && !isInputEmpty) {
      setIsInputEmpty(true);
      return;
    }

    if (input.length > 0 && isInputEmpty) {
      setIsInputEmpty(false);
    }
  };

  const renderMenu = (
    results: TypeaheadResult<Option>[],
    menuProps: TypeaheadMenuProps<Option>
  ): React.ReactNode => {
    const { text } = menuProps;
    const defaultSort = (a: Option, b: Option): number =>
      a.label.length - b.label.length || a.label.localeCompare(b.label);

    if (hideOptionsByDefault && isInputEmpty) {
      return <></>;
    }

    let filteredOptions = [
      ...results
        .filter(
          (result) => result.label.toLowerCase().indexOf(text?.toLowerCase() || '') !== -1
        )
        .sort(defaultSort),
    ];

    if (options.length > maxResults) {
      filteredOptions = filteredOptions.slice(0, maxResults);
    }

    if (filteredOptions.length === 0) {
      return (
        <StyledMenu {...menuProps}>
          <HiddenMenuDescription id={menuDescriptionId}>
            {noMatchesText}
          </HiddenMenuDescription>
          <DisabledMenuItem option="" position={1} disabled>
            {noMatchesText}
          </DisabledMenuItem>
        </StyledMenu>
      );
    }

    return (
      <StyledMenu {...menuProps}>
        <HiddenMenuDescription id={menuDescriptionId}>
          Suggestions are available, use arrow key to navigate the suggestion list
        </HiddenMenuDescription>
        {filteredOptions.map((option, index) => (
          <StyledMenuItem option={option} position={index} key={option.id}>
            {option.label}
          </StyledMenuItem>
        ))}
      </StyledMenu>
    );
  };

  const renderToken = (selectedItem: Option, props: TokenProps): React.ReactNode => (
    <StyledToken {...(props as MultiTypeAheadTokenProps)} option={selectedItem}>
      {selectedItem.label}
    </StyledToken>
  );
  return (
    <>
      <ClearFiltersButton onClick={() => setSelectedOptions([])}>
        Clear filters
      </ClearFiltersButton>
      <StyledTypeahead
        id={`${id}-multiselect-typeahead`}
        options={options}
        selected={selectedOptions}
        renderMenu={renderMenu}
        onChange={setSelectedOptions}
        onInputChange={handleOnInputChange}
        minLength={minLength}
        multiple
        inputProps={rest}
        labelKey="label"
        renderToken={renderToken}
      />
    </>
  );
};
